import React from 'react';
import { MentorModule } from '../../types/mentorship';
import { PlayCircle, CheckCircle2 } from 'lucide-react';

interface ModulePosterCardProps {
  module: MentorModule;
  isSelected?: boolean;
  onSelect: () => void;
  accentColor?: string;
}

export const ModulePosterCard: React.FC<ModulePosterCardProps> = ({
  module,
  isSelected = false,
  onSelect,
  accentColor = '#0FFF55',
}) => {
  const totalLessons = module.lessons.length;
  const completedLessons = module.lessons.filter((l) => l.isCompleted).length;
  const progressPercent = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  return (
    <div
      onClick={onSelect}
      className={`group relative flex-shrink-0 w-44 sm:w-52 md:w-60 lg:w-64 aspect-[3/5] rounded-2xl overflow-hidden cursor-pointer select-none border transition-all duration-300 transform hover:-translate-y-2 ${
        isSelected
          ? 'border-[#0FFF55] shadow-2xl scale-[1.02]'
          : 'border-white/10 hover:border-[#0FFF55]/50 bg-[#17181c]'
      }`}
      style={{
        boxShadow: isSelected
          ? `0 0 25px rgba(15, 255, 85, 0.35), 0 10px 30px rgba(0,0,0,0.8)`
          : undefined,
      }}
    >
      {/* Module Poster Image */}
      <img
        src={module.coverImageUrl}
        alt={module.title}
        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 ease-out"
      />

      {/* Dark Texture Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0d0e11] via-[#0d0e11]/60 to-black/30" />

      {/* Soft Green Glow Hover Accent (No bluish tint) */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300 pointer-events-none bg-gradient-to-t from-[#0FFF55]/30 to-transparent" />

      {/* Content Container */}
      <div className="absolute inset-0 p-4 sm:p-5 flex flex-col justify-between z-10">
        {/* Top Badge */}
        <div className="flex items-center justify-between">
          <span className="px-2.5 py-1 rounded-md bg-black/70 backdrop-blur-md border border-white/10 text-[9px] font-extrabold text-gray-200 uppercase tracking-widest">
            MÓDULO {module.order}
          </span>

          {progressPercent === 100 ? (
            <span className="p-1 rounded-full bg-emerald-500/20 text-[#0FFF55] border border-[#0FFF55]/40">
              <CheckCircle2 className="w-3.5 h-3.5" />
            </span>
          ) : (
            <span className="text-[10px] font-semibold text-gray-400">
              {totalLessons} {totalLessons === 1 ? 'aula' : 'aulas'}
            </span>
          )}
        </div>

        {/* Center Title (Hubla Bold Uppercase Style) */}
        <div className="my-auto py-2">
          <h3 className="text-sm sm:text-base md:text-lg font-black text-white uppercase tracking-tight leading-snug drop-shadow-md group-hover:text-[#0FFF55] transition-colors">
            {module.title}
          </h3>
          {module.description && (
            <p className="text-[10px] text-gray-400 line-clamp-2 mt-1 font-light leading-relaxed">
              {module.description}
            </p>
          )}
        </div>

        {/* Bottom Progress Bar & Action */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between text-[10px] text-gray-400 font-semibold">
            <span className="flex items-center gap-1 group-hover:text-white transition-colors">
              <PlayCircle className="w-3 h-3 text-[#0FFF55]" />
              Acessar
            </span>
            <span>{progressPercent}%</span>
          </div>

          {/* Progress Bar Line */}
          <div className="w-full h-1 rounded-full bg-white/15 overflow-hidden">
            <div
              className="h-full rounded-full bg-[#0FFF55] transition-all duration-500 shadow-[0_0_10px_#0FFF55]"
              style={{
                width: `${progressPercent}%`,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
